import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Typography } from '@mui/material';
import { Link } from 'daaas-ui';
import Icon from '@mdi/react';
import styled from '@emotion/styled';
import { PropTypes } from 'prop-types';
import { mdiOpenInNew } from '@mdi/js';

// Styles for the card
const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  height: '100%',
  width: '100%',

  '& .toolcard': {
    width: '100%',
  },

  '& .toolcard .toolcard__container': {
    display: 'flex',
    flexDirection: 'column',
  },

  '& .toolcard .toolcard__heading__container': {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    flexGrow: '1',
    alignItems: 'center',
    width: '100%',
  },

  '& .toolcard .toolcard__heading__container .toolcard__heading__logo': {
    flexBasis: 'auto',
  },

  '& .toolcard .toolcard__heading__container .toolcard__heading__logo img, & .toolcard .toolcard__heading__container .toolcard__heading__logo svg':
    {
      width: '3.75rem !important',
      objectFit: 'fill',
      overflow: 'hidden',
      borderRadius: '10px',
    },

  '& .toolcard .toolcard__heading__container .toolcard__heading__title': {
    flexGrow: '1',
    marginLeft: theme.spacing(2),
  },

  '& .toolcard .toolcard__heading__container .toolcard__heading__link': {
    marginTop: theme.spacing(0.5),
    paddingLeft: theme.spacing(2.5),
    '& button': {
      textAlign: 'left',
      verticalAlign: 'baseline',
    },
    '& svg': {
      marginLeft: theme.spacing(1),
      verticalAlign: 'middle',
    },
  },

  '& .toolcard .toolcard__content__container': {
    margin: theme.spacing(2, 0, 0, 0),
    width: '100%',
  },
}));

export default function ToolCard({
  bordered = false,
  icon = <img src="https://picsum.photos/200/200" alt="" loading="lazy" />,
  heading = { text: 'No heading provided', linkTo: '/' },
  links = null,
  children = <p>No Children Provided</p>,
}) {
  const { t } = useTranslation(['common']);

  // Is the link internal or external
  // If a link is external we have to use an a but if it is
  // internal we need to use a router link
  const isInternalURL = (to) => {
    try {
      const url = new URL(to, window.location.origin);
      return url.hostname === window.location.hostname;
    } catch {
      return false;
    }
  };

  const renderLink = (link) => {
    if (link) {
      // Is the link a Link
      if (link.linkTo) {
        if (isInternalURL(link.linkTo)) {
          return (
            <Link key={link} href={link.linkTo}>
              {link.text}
            </Link>
          );
        }
        return (
          <>
            <Link key={link} href={link.linkTo} target="_blank">
              {link.text}
              <span className="screen-reader-text">({t('newTab')})</span>
            </Link>
            <Icon path={mdiOpenInNew} size={0.8} />
          </>
        );
      }
      // Is the link a button
      return (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <Link
          key={link}
          component="button"
          onClick={() => {
            link.linkFunction();
          }}
        >
          {link.text}
        </Link>
      );
    }
    return null;
  };

  const renderLinksList = () => {
    return links.map((link) => {
      if (link.linkTo) {
        return (
          <li key={link.linkTo}>
            <Typography>{renderLink(link)}</Typography>
          </li>
        );
      }
      return (
        <li key={link.linkFunction}>
          <Typography>{renderLink(link)}</Typography>
        </li>
      );
    });
  };

  return (
    <Root>
      <Card
        variant="outlined"
        className={`toolcard das-paper ${bordered ? 'bordered' : ''}`}
      >
        <CardContent>
          <div className="toolcard__container">
            <div className="toolcard__heading__container">
              <div className="toolcard__heading__logo">{icon}</div>
              <div className="toolcard__heading__title">
                {heading.linkTo ? (
                  <Typography variant="h3">
                    {isInternalURL(heading.linkTo) ? (
                      <Link href={heading.linkTo}>{heading.text}</Link>
                    ) : (
                      <Link href={heading.linkTo} target="_blank">
                        {heading.text}
                        <span className="screen-reader-text">
                          {t('newTab')}
                        </span>
                      </Link>
                    )}
                  </Typography>
                ) : (
                  <Typography variant="h3">{heading.text}</Typography>
                )}
                {links && (
                  <ul className="toolcard__heading__link">
                    {renderLinksList()}
                  </ul>
                )}
              </div>
            </div>
            <div className="toolcard__content__container">{children}</div>
          </div>
        </CardContent>
      </Card>
    </Root>
  );
}

// Prop Validation
ToolCard.propTypes = {
  bordered: PropTypes.bool,
  icon: PropTypes.element,
  heading: PropTypes.PropTypes.shape({
    text: PropTypes.string,
    linkTo: PropTypes.string,
  }),
  links: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        text: PropTypes.node,
        linkTo: PropTypes.string,
      }),
      PropTypes.shape({
        text: PropTypes.node,
        linkFunction: PropTypes.func,
      }),
    ])
  ),
  children: PropTypes.element,
};
